import { useEffect, useState } from "react";
import { CheckCircle, AlertTriangle, XCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { ExportScope } from "./scope-selection";

interface ValidationStepProps {
  scope: ExportScope;
  selectedFormat: string;
  onNext: () => void;
  onBack: () => void;
}

interface ValidationCheck {
  id: string;
  label: string;
  status: 'pending' | 'success' | 'warning' | 'error';
  message: string;
  count?: number;
}

export const ValidationStep = ({ scope, selectedFormat, onNext, onBack }: ValidationStepProps) => {
  const [checks, setChecks] = useState<ValidationCheck[]>([
    { id: 'transactions', label: 'Transaction Count', status: 'pending', message: 'Checking...' },
    { id: 'classification', label: 'Classification Status', status: 'pending', message: 'Checking...' },
    { id: 'balance', label: 'Debit/Credit Balance', status: 'pending', message: 'Checking...' },
    { id: 'duplicates', label: 'Duplicate Detection', status: 'pending', message: 'Checking...' },
    { id: 'accounts', label: 'Account Validation', status: 'pending', message: 'Checking...' },
  ]);
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    runValidation();
  }, [scope]);

  const runValidation = async () => {
    setIsValidating(true);

    try {
      // Check 1: Transaction Count
      const { count: totalCount, error: countError } = await supabase
        .from('transactions')
        .select('*', { count: 'exact', head: true })
        .gte('transaction_date', scope.startDate)
        .lte('transaction_date', scope.endDate);

      if (countError) throw countError;

      updateCheck('transactions', {
        status: totalCount && totalCount > 0 ? 'success' : 'warning',
        message: totalCount ? `${totalCount} transactions found` : 'No transactions in date range',
        count: totalCount || 0
      });

      // Check 2: Classification Status
      const { count: unclassifiedCount } = await supabase
        .from('transactions')
        .select('*', { count: 'exact', head: true })
        .gte('transaction_date', scope.startDate)
        .lte('transaction_date', scope.endDate)
        .or('account_number.is.null,account_number.eq.');

      const classifiedPercentage = totalCount ? ((totalCount - (unclassifiedCount || 0)) / totalCount * 100) : 0;

      updateCheck('classification', {
        status: classifiedPercentage === 100 ? 'success' : classifiedPercentage > 80 ? 'warning' : 'error',
        message: `${classifiedPercentage.toFixed(1)}% classified (${unclassifiedCount || 0} unclassified)`,
        count: unclassifiedCount || 0
      });

      // Check 3: Debit/Credit Balance
      const { data: transactions } = await supabase
        .from('transactions')
        .select('amount, transaction_type')
        .gte('transaction_date', scope.startDate)
        .lte('transaction_date', scope.endDate);

      let totalDebit = 0;
      let totalCredit = 0;

      transactions?.forEach(t => {
        const amount = Math.abs(parseFloat(t.amount?.toString() || '0'));
        if (t.transaction_type === 'debit' || parseFloat(t.amount?.toString() || '0') < 0) {
          totalDebit += amount;
        } else {
          totalCredit += amount;
        }
      });

      const difference = Math.abs(totalDebit - totalCredit);
      const isBalanced = difference < 0.01;

      updateCheck('balance', {
        status: isBalanced ? 'success' : 'warning',
        message: isBalanced
          ? `Balanced (CHF ${totalDebit.toFixed(2)})`
          : `Difference: CHF ${difference.toFixed(2)}`
      });

      // Check 4: Duplicates
      const { data: duplicates } = await supabase
        .from('transactions')
        .select('transaction_date, amount, description')
        .gte('transaction_date', scope.startDate)
        .lte('transaction_date', scope.endDate);

      const seen = new Set();
      let duplicateCount = 0;

      duplicates?.forEach(t => {
        const key = `${t.transaction_date}_${t.amount}_${t.description}`;
        if (seen.has(key)) {
          duplicateCount++;
        } else {
          seen.add(key);
        }
      });

      updateCheck('duplicates', {
        status: duplicateCount === 0 ? 'success' : 'warning',
        message: duplicateCount === 0
          ? 'No duplicates detected'
          : `${duplicateCount} potential duplicates found`,
        count: duplicateCount
      });

      // Check 5: Account Validation
      const { data: invalidAccounts } = await supabase
        .from('transactions')
        .select('account_number')
        .gte('transaction_date', scope.startDate)
        .lte('transaction_date', scope.endDate)
        .not('account_number', 'is', null)
        .not('account_number', 'eq', '');

      const accountNumbers = new Set(invalidAccounts?.map(t => t.account_number) || []);
      const invalidAccountsCount = Array.from(accountNumbers).filter(
        acc => !acc || acc.length < 4 || isNaN(Number(acc))
      ).length;

      updateCheck('accounts', {
        status: invalidAccountsCount === 0 ? 'success' : 'warning',
        message: invalidAccountsCount === 0
          ? `${accountNumbers.size} valid accounts`
          : `${invalidAccountsCount} potentially invalid account numbers`
      });

    } catch (error) {
      console.error('Validation error:', error);
      updateCheck('transactions', {
        status: 'error',
        message: 'Validation failed. Please try again.'
      });
    } finally {
      setIsValidating(false);
    }
  };

  const updateCheck = (id: string, updates: Partial<ValidationCheck>) => {
    setChecks(prev => prev.map(check =>
      check.id === id ? { ...check, ...updates } : check
    ));
  };

  const getStatusIcon = (status: ValidationCheck['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-orange-600" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Loader2 className="h-5 w-5 text-brand-text-secondary animate-spin" />;
    }
  };

  const getStatusColor = (status: ValidationCheck['status']) => {
    switch (status) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'warning':
        return 'bg-orange-50 border-orange-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const hasErrors = checks.some(c => c.status === 'error');
  const hasWarnings = checks.some(c => c.status === 'warning');

  return (
    <div>
      <h3 className="text-lg font-bold text-brand-primary mb-2 text-center">
        Pre-Export Validation
      </h3>
      <p className="text-brand-text-secondary text-sm mb-6 text-center">
        Checking data quality and compliance
      </p>

      <div className="max-w-2xl mx-auto space-y-3">
        {checks.map(check => (
          <div
            key={check.id}
            className={`p-4 rounded-xl border-2 ${getStatusColor(check.status)}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getStatusIcon(check.status)}
                <div>
                  <h5 className="font-medium text-brand-text-primary">{check.label}</h5>
                  <p className="text-sm text-brand-text-secondary">{check.message}</p>
                </div>
              </div>
              {check.count !== undefined && check.count > 0 && (
                <span className="px-2 py-1 bg-white rounded-lg text-sm font-medium">
                  {check.count}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {!isValidating && (
        <div className="max-w-2xl mx-auto mt-6">
          {hasErrors && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 text-red-800">
                <XCircle className="h-5 w-5" />
                <p className="font-medium">
                  Critical errors detected. Please fix issues before exporting.
                </p>
              </div>
            </div>
          )}
          {hasWarnings && !hasErrors && (
            <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 text-orange-800">
                <AlertTriangle className="h-5 w-5" />
                <p className="font-medium">
                  Some warnings detected. Review before proceeding.
                </p>
              </div>
            </div>
          )}
          {!hasErrors && !hasWarnings && (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="h-5 w-5" />
                <p className="font-medium">
                  All validation checks passed! Ready to export.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-center gap-4 mt-8">
        <button
          onClick={onBack}
          disabled={isValidating}
          className="px-6 py-3 bg-brand-secondary text-brand-primary rounded-lg font-medium hover:bg-brand-secondary/80 transition-colors disabled:opacity-50"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={isValidating || hasErrors}
          className="px-6 py-3 bg-brand-primary text-white rounded-lg font-medium hover:bg-brand-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isValidating ? 'Validating...' : 'Continue to Export'}
        </button>
      </div>
    </div>
  );
};
